/** TipTap rich text editor for wiki entry fields. */
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useCallback, useEffect } from 'react';
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, Link as LinkIcon, Quote, Code } from 'lucide-react';

const toolbarBtnClass = "p-1.5 rounded hover:bg-slate-700 text-slate-300 transition-colors";
const activeBtnClass = "p-1.5 rounded bg-amber-500/20 text-amber-400 transition-colors";

export default function MarkdownEditor({ value, onChange, placeholder, readOnly = false }) {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Link.configure({ openOnClick: false }),
            Placeholder.configure({ placeholder: placeholder || 'Start typing...', emptyEditorClass: 'text-slate-500' }),
        ],
        content: value || '',
        editable: !readOnly,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange?.(html);
        },
    });

    // Sync external value changes
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            const pos = editor.view.state.selection.from;
            editor.commands.setContent(value || '');
            editor.commands.setTextSelection(pos);
        }
    }, [value, editor]);

    const addWikiLink = useCallback(() => {
        const slug = prompt('Enter wiki entry slug (e.g. hikvision-ds-2cd2047):');
        if (slug) {
            editor.chain().focus().insertContent(`[[${slug}]]`).run();
        }
    }, [editor]);

    if (!editor) return null;

    return (
        <div className={`border rounded-lg overflow-hidden ${readOnly ? 'border-slate-700' : 'border-slate-600'}`}>
            {!readOnly && (
                <div className="flex items-center gap-1 p-2 bg-slate-800 border-b border-slate-700 flex-wrap">
                    <button
                        type="button"
                        className={editor.isActive('bold') ? activeBtnClass : toolbarBtnClass}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        title="Bold"
                    ><Bold size={16} /></button>
                    <button
                        type="button"
                        className={editor.isActive('italic') ? activeBtnClass : toolbarBtnClass}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        title="Italic"
                    ><Italic size={16} /></button>
                    <button
                        type="button"
                        className={editor.isActive('heading', { level: 1 }) ? activeBtnClass : toolbarBtnClass}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        title="Heading 1"
                    ><Heading1 size={16} /></button>
                    <button
                        type="button"
                        className={editor.isActive('heading', { level: 2 }) ? activeBtnClass : toolbarBtnClass}
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        title="Heading 2"
                    ><Heading2 size={16} /></button>
                    <div className="w-px h-5 bg-slate-600 mx-1" />
                    <button
                        type="button"
                        className={editor.isActive('bulletList') ? activeBtnClass : toolbarBtnClass}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        title="Bullet list"
                    ><List size={16} /></button>
                    <button
                        type="button"
                        className={editor.isActive('orderedList') ? activeBtnClass : toolbarBtnClass}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        title="Ordered list"
                    ><ListOrdered size={16} /></button>
                    <button
                        type="button"
                        className={editor.isActive('blockquote') ? activeBtnClass : toolbarBtnClass}
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        title="Quote"
                    ><Quote size={16} /></button>
                    <button
                        type="button"
                        className={editor.isActive('codeBlock') ? activeBtnClass : toolbarBtnClass}
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        title="Code block"
                    ><Code size={16} /></button>
                    <div className="w-px h-5 bg-slate-600 mx-1" />
                    <button type="button" className={toolbarBtnClass} onClick={addWikiLink} title="[[Wiki link]]">
                        <LinkIcon size={16} />
                    </button>
                </div>
            )}
            <div className="min-h-[120px] p-4 prose prose-invert prose-sm max-w-none bg-slate-900">
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}